import { trpc } from '@/trpc/react';
import Drawer from 'expo-router/drawer';
import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { ScrollView } from 'react-native';
import { z } from 'zod';
import { router, useLocalSearchParams } from "expo-router";
import Toast from "react-native-toast-message";
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormField, FormLabel } from '@/components/ui/form';
import { Button, Textarea } from '@/components/ui';
import { useEffect } from 'react';
import { Text } from "react-native";
import { useCrew } from '@/context/CrewProvider';
import { addWeeks } from 'date-fns'
import colors from '@/constants/Colors';

const timesheetIssueSchema = z.object({
  query: z.string().min(1),
})

export default function TimeSheetIssue() {
  const { currentCrewMember } = useCrew();

  const { id, jobCrewId } = useLocalSearchParams();

  const { data: timesheetIssue } = trpc.jobTimesheet.getTimesheetIssue.useQuery(Number(id));

  const form = useForm<z.infer<typeof timesheetIssueSchema>>({
    resolver: zodResolver(timesheetIssueSchema),
    defaultValues: {
      query: timesheetIssue?.data?.query || ''
    }
  });

  const { mutate: reportTimesheetIssue } = trpc.jobTimesheet.reportTimesheetIssue.useMutation({
    onSuccess: () => {
      router.replace("/(app)/timesheets");
      Toast.show({
        type: 'success',
        text1: `Timesheet issue reported`,
      });
    },
  });

  const { mutate: updateTimesheetIssue } = trpc.jobTimesheet.updateTimesheetIssue.useMutation({
    onSuccess: () => {
      router.replace("/(app)/timesheets");
      Toast.show({
        type: 'success',
        text1: `Timesheet issue updated`,
      });
    },
  });

  const { mutate: createTask } = trpc.task.createTask.useMutation({
    onSuccess(_response) { },
  });

  async function onSubmit(data: z.infer<typeof timesheetIssueSchema>) {
    if (timesheetIssue?.data?.id) {
      updateTimesheetIssue({
        id: timesheetIssue.data.id,
        query: data.query,
      });
    } else {
      reportTimesheetIssue({
        jobCrewId: Number(jobCrewId),
        query: data.query,
      });

      createTask({
        title: `New Crew Timesheet Issue for ${currentCrewMember?.name}`,
        description: `New Crew Timesheet Issue for ${currentCrewMember?.name}`,
        priority: 1,
        tags: ["crew timesheet issue"],
        due_date: addWeeks(new Date(), 1),
        crew_id: currentCrewMember?.id,
        state_name: "Open",
        type: "timesheet_issue.create",
        team_id: 2
      })
    }
  }

  useEffect(() => {
    if (timesheetIssue?.data?.query) {
      form.setValue('query', timesheetIssue.data.query);
    }
  }, [timesheetIssue]);

  return (
    <>
      <Drawer.Screen
        options={{
          title: `Timesheet Issue`,
          headerStyle: {
            backgroundColor: colors.primary,
          },
          headerTintColor: colors.accent,
        }}
      />
      <ScrollView className="flex-1 p-4">
        <Form {...form}>
          <Card className='mt-4'>
            <CardHeader>
              <CardTitle>Report Timesheet Issue</CardTitle>
            </CardHeader>
            <CardContent>
              <FormLabel>Query</FormLabel>
              <FormField
                control={form.control}
                name="query"
                render={({ field }) => (
                  <Textarea
                    {...field}
                    onChangeText={(value) => {
                      field.onChange(value);
                    }}
                    value={field.value || ''}
                    editable={!timesheetIssue?.data?.resolved}
                  />
                )}
              />
              {timesheetIssue?.data?.response ? (
                <Text className='mt-4'>Response: {timesheetIssue.data.response}</Text>
              ) : null}

              {timesheetIssue?.data?.resolved ? (
                <Text className='mt-4'>Resolved: {timesheetIssue.data.resolved ? 'Yes' : 'No'}</Text>
              ) : null}

              {!timesheetIssue?.data?.resolved ? (
                <Button className='mt-4' size="default"
                  variant="crew"
                  onPress={form.handleSubmit(onSubmit)}
                >
                  Submit
                </Button>
              ) : null}
            </CardContent>
          </Card>
        </Form>
      </ScrollView>
    </>
  );
}
